/**
 * Error logging and monitoring utility
 * Supports multiple log levels and can be integrated with services like Sentry, LogRocket, etc.
 */

export type LogLevel = "debug" | "info" | "warn" | "error";

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: Date;
  context?: Record<string, any>;
  error?: Error;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs: number = 100;
  private isDevelopment: boolean = process.env.NODE_ENV === "development";

  /**
   * Log a debug message (development only)
   */
  debug(message: string, context?: Record<string, any>) {
    if (this.isDevelopment) {
      console.debug(`[DEBUG] ${message}`, context);
    }
    this.addLog("debug", message, context);
  }

  /**
   * Log an info message
   */
  info(message: string, context?: Record<string, any>) {
    console.info(`[INFO] ${message}`, context);
    this.addLog("info", message, context);
  }

  /**
   * Log a warning
   */
  warn(message: string, context?: Record<string, any>) {
    console.warn(`[WARN] ${message}`, context);
    this.addLog("warn", message, context);
  }

  /**
   * Log an error with optional error object
   */
  error(message: string, error?: Error, context?: Record<string, any>) {
    console.error(`[ERROR] ${message}`, error || "", context);
    this.addLog("error", message, context, error);

    // Send to external monitoring service in production
    if (!this.isDevelopment) {
      this.reportToMonitoring(message, error, context);
    }
  }

  /**
   * Add a log entry to internal buffer
   */
  private addLog(
    level: LogLevel,
    message: string,
    context?: Record<string, any>,
    error?: Error
  ) {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date(),
      context,
      error,
    };

    this.logs.push(entry);

    // Keep only recent logs to avoid memory issues
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  /**
   * Get recent logs for debugging
   */
  getRecentLogs(count: number = 20): LogEntry[] {
    return this.logs.slice(-count);
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Report error to external monitoring service
   * This is a placeholder for Sentry, LogRocket, or similar services
   * @example
   * // Install: npm install @sentry/react
   * // In main.tsx:
   * import * as Sentry from "@sentry/react";
   * Sentry.init({ dsn: process.env.VITE_SENTRY_DSN });
   */
  private reportToMonitoring(
    message: string,
    error?: Error,
    context?: Record<string, any>
  ) {
    // Check if Sentry is available
    if ((window as any).__SENTRY_RELEASE__) {
      try {
        if ((window as any).Sentry) {
          (window as any).Sentry.captureException(error || new Error(message), {
            tags: { context_type: "app_error" },
            extra: context,
          });
        }
      } catch (e) {
        console.error("Failed to report to Sentry:", e);
      }
    }

    // Alternative: Send to custom logging endpoint
    // this.sendToCustomEndpoint(message, error, context);
  }

  /**
   * Send logs to a custom logging endpoint
   * Useful for self-hosted logging solutions
   */
  private async sendToCustomEndpoint(
    message: string,
    error?: Error,
    context?: Record<string, any>
  ) {
    if (!import.meta.env.VITE_LOG_ENDPOINT) {
      return;
    }

    try {
      await fetch(import.meta.env.VITE_LOG_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          error: error?.message,
          stack: error?.stack,
          context,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        }),
      });
    } catch (e) {
      // Silently fail to avoid creating new errors
      console.error("Failed to send logs:", e);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

/**
 * Error tracking decorator for async functions
 * Automatically logs errors that occur during function execution
 * @example
 * @trackError("userSubmission")
 * async function submitForm(data) { ... }
 */
export function trackError(context: string) {
  return function (
    target: any,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    descriptor.value = async function (...args: any[]) {
      try {
        return await originalMethod.apply(this, args);
      } catch (error) {
        logger.error(`Error in ${context}.${propertyKey}`, error as Error, {
          args: args.length,
        });
        throw error;
      }
    };

    return descriptor;
  };
}
